import { useState } from "react";
import {
  EnvelopeIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon,
  CloudArrowUpIcon,
  BoltSlashIcon,
  BoltIcon,
  TrashIcon,
  UserGroupIcon,
  BugAntIcon,
} from "@heroicons/react/24/outline";
import { NODE_TYPE } from "../../services/Constants";
import DebugDetailsDialog from "../dialogs/DebugDetailsDialog";
import { Button } from "../Button";

export const ACTION_TYPE_META = {
  [NODE_TYPE.EMAIL]: {
    label: "Email",
    icon: EnvelopeIcon,
    pillClasses: "border border-blue-100 bg-blue-50 text-blue-700",
  },
  [NODE_TYPE.DELAY]: {
    label: "Delay",
    icon: ClockIcon,
    pillClasses: "border border-amber-100 bg-amber-50 text-amber-700",
  },
  [NODE_TYPE.SMS]: {
    label: "SMS",
    icon: ChatBubbleLeftRightIcon,
    pillClasses: "border border-emerald-100 bg-emerald-50 text-emerald-700",
  },
  [NODE_TYPE.PUSH_NOTIFICATION]: {
    label: "Push",
    icon: BellAlertIcon,
    pillClasses: "border border-purple-100 bg-purple-50 text-purple-700",
  },
  [NODE_TYPE.WEBHOOK]: {
    label: "Webhook",
    icon: CloudArrowUpIcon,
    pillClasses: "border border-slate-200 bg-slate-50 text-slate-700",
  },
  [NODE_TYPE.TRIGGER]: {
    label: "Goal",
    icon: BoltIcon,
    pillClasses: "border border-blue-100 bg-blue-50 text-blue-700",
  },
  default: {
    label: "Action",
    icon: BoltSlashIcon,
    pillClasses: "border border-slate-200 bg-slate-50 text-slate-600",
  },
};

export const CARD_BASE_CLASSES =
  "min-w-80 max-w-80 min-h-32 cursor-pointer rounded-xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-primary-500";

export const ACTION_DELAY_CARD_BASE_CLASSES =
  "min-w-32 min-h-32 cursor-pointer rounded-xl bg-white p-4 transition hover:-translate-y-0.5 hover:border-primary-500";

export const ActionCard = ({ action, onClick, onDelete, className = "", nodeId, segmentEdges, isOnboarding = false }) => {
  const [debugOpen, setDebugOpen] = useState(false);
  const meta = ACTION_TYPE_META[action.type] || ACTION_TYPE_META.default;
  const Icon = meta.icon;
  const isReady = isOnboarding ? true : (action.ready !== false);

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(action.id);
    }
  };

  const handleDebugClick = (e) => {
    e.stopPropagation();
    setDebugOpen(true);
  };

  if ( action.type === NODE_TYPE.DELAY){
    const delayUnit = action.delayUnit || "days";
    const delay = action.delay || 0;
    
    // Format unit name with proper pluralization
    const formatUnit = (unit, value) => {
      const unitMap = {
        minutes: value === 1 ? "minute" : "minutes",
        hours: value === 1 ? "hour" : "hours",
        days: value === 1 ? "day" : "days",
      };
      return unitMap[unit] || (value === 1 ? unit.slice(0, -1) : unit);
    };
    
    return (
      <div className={`${ACTION_DELAY_CARD_BASE_CLASSES} ${className} relative group`}  onClick={onClick} data-node-type={NODE_TYPE.DELAY} data-action-id={action.id} data-delay={delay} data-action-type={action.type} >
        <div className="mt-9 text-sm font-semibold text-slate-900">
          Wait {delay} {formatUnit(delayUnit, delay)}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`${CARD_BASE_CLASSES} ${className} relative group ${!isReady ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={isReady ? onClick : undefined}
        data-action-type={action.type}
        data-action-id={action.id}
        data-node-type={NODE_TYPE.ACTION}
      >
       
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {!isReady ? (
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
          ) : (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <button
                type="button"
                onClick={handleDebugClick}
                className="p-1.5 rounded-md hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                title="Debug details"
              >
                <BugAntIcon className="h-4 w-4" />
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500"
                  title="Delete action"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.pillClasses}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {meta.label}
          </span>
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-900">
          {action.title || action.description}
        </div>
        {action.outcomeGoal && (
          <p className="mt-1 text-xs text-slate-500">Goal: {action.outcomeGoal}</p>
        )}
        {action.description && (
          <p className="mt-1 text-xs text-slate-500">{action.description}</p>
        )}
        {action.emailSubject && (
          <p className="mt-1 text-xs text-slate-500">{action.emailSubject}</p>
        )}
      </div>
      <DebugDetailsDialog
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        data={action}
        nodeType={NODE_TYPE.ACTION}
        nodeId={nodeId}
        segmentEdges={segmentEdges}
      />
    </>
  );
};

export const TriggerCard = ({ trigger, onClick, onDelete, className = "", nodeId, segmentEdges, isOnboarding = false }) => {
  const [debugOpen, setDebugOpen] = useState(false);
  const isReady = isOnboarding ? true : (trigger.ready !== false);
  let actionsCount = trigger.actions?.length || 0;

  let delayActionCount = 0;
  trigger.actions?.forEach(action => {
    if (action.type === NODE_TYPE.DELAY) {
      delayActionCount += action.delay;
    }
  });

  if (delayActionCount > 0) {
    actionsCount -= delayActionCount;
  }

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(trigger.id);
    }
  };

  const handleDebugClick = (e) => {
    e.stopPropagation();
    setDebugOpen(true);
  };

  return (
    <>
      <div
        className={`${CARD_BASE_CLASSES} ${className} relative group ${!isReady ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={isReady ? onClick : undefined}
        data-node-type={NODE_TYPE.TRIGGER}
        data-trigger-id={trigger.id}
        data-trigger-title={trigger.title}
        data-actions-count={actionsCount}
      >
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {!isReady ? (
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
          ) : (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
              <button
                type="button"
                onClick={handleDebugClick}
                className="p-1.5 rounded-md hover:bg-amber-50 text-slate-400 hover:text-amber-600"
                title="Debug details"
              >
                <BugAntIcon className="h-4 w-4" />
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500"
                  title="Delete segment"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
            <BoltIcon className="h-3.5 w-3.5 text-amber-500" />
             Goal
          </span>
          {actionsCount > 0 && (
            <span className="text-[11px] font-medium text-slate-500">
              {actionsCount} action{actionsCount === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-900">
          {trigger.outcomeGoal || trigger.title || "Goal"}
        </div>
        {trigger.description && (
          <p className="mt-1 text-xs text-slate-500">{trigger.description}</p>
        )}
      </div>
      <DebugDetailsDialog
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        data={trigger}
        nodeType={NODE_TYPE.TRIGGER}
        nodeId={nodeId}
        segmentEdges={segmentEdges}
      />
    </>
  );
};

export const StakeholderCard = ({ stakeholder, onClick, onAddTrigger, className = "", nodeId, segmentEdges }) => {
  const [debugOpen, setDebugOpen] = useState(false);
  const triggerCount = stakeholder.triggers?.length || 0;

  const handleAddTriggerClick = (e) => {
    e.stopPropagation();
    if (onAddTrigger) {
      onAddTrigger(stakeholder.id, stakeholder.title || stakeholder.name);
    }
  };

  const handleDebugClick = (e) => {
    e.stopPropagation();
    setDebugOpen(true);
  };

  return (
    <>
      <div
        className={`${CARD_BASE_CLASSES} ${className} relative group`}
        onClick={onClick}
        data-node-type={NODE_TYPE.STAKEHOLDER}
        data-stakeholder-id={stakeholder.id}
        data-stakeholder-name={stakeholder.name || stakeholder.title}
        data-trigger-count={triggerCount}
      >
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={handleDebugClick}
            className="p-1.5 rounded-md hover:bg-amber-50 text-slate-400 hover:text-amber-600"
            title="Debug details"
          >
            <BugAntIcon className="h-4 w-4" />
          </button>
          {onAddTrigger && (
            <Button
              type="button"
              onClick={handleAddTriggerClick}
              color="primary"
              size="sm"
              title="Add goal"
            >
              + Add Goal
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700">
            <UserGroupIcon className="h-3.5 w-3.5 text-indigo-500" />
            Stakeholder
          </span>
          <span className="text-[11px] font-medium text-slate-500">
            {triggerCount} goal{triggerCount === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-900">
          {stakeholder.title || stakeholder.name || "Stakeholder"}
        </div>
      </div>
      <DebugDetailsDialog
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        data={stakeholder}
        nodeType={NODE_TYPE.STAKEHOLDER}
        nodeId={nodeId}
        segmentEdges={segmentEdges}
      />
    </>
  );
};
